<!--
    `hasRows` is what decides whether loading/error replaces the body or
    merely overlays it (via `appBlockUi` on the wrapper below). On the very
    first load there is nothing to preserve, so the full-replace state below
    is fine. Once rows exist, replacing them on every lazy re-fetch
    (sort/search/page) would drop every <td> that was pinning the <th>
    widths under `table-layout: auto` — the header row then reflows to the
    width of its own text and jumps back once data returns. Keeping the same
    rows mounted underneath the block-ui overlay keeps the columns' measured
    content constant across the request, so the header never moves.
-->
@let hasRows = paginatedData().length > 0;

<!-- ===========================
    caption (top)
=========================== -->
@if (showCaption() && captionTop()) {
    <ng-container *ngTemplateOutlet="tableCaption" />
}

<!-- ===========================
    table responsive wrapper
    `appBlockUi` overlays the shared loader over this box while a lazy
    re-fetch is in flight and previous rows are still mounted underneath
    (see the tbody comment below).

    `skipSelfPositionValue` opts out of the directive's own
    `[class.overflow-hidden]` host binding: this wrapper is a horizontally
    scrollable `.table-responsive` when there are enough columns, and
    toggling `overflow: hidden` on it for the blocked duration hides/shows
    its scrollbar on every load — the same class of layout shift this
    overlay exists to prevent, just on the horizontal axis. `position-
    relative` (which the overlay's `inset: 0` still needs) is supplied here
    instead, unconditionally, since skipping the input turns both off.
=========================== -->
<div
    class="position-relative"
    [class]="responsiveClass()"
    [appBlockUi]="loading() && hasRows"
    [skipSelfPositionValue]="true">
    
    <table
        class="table"
        [class]="buildTableClassStyle()"
        [attr.aria-busy]="loading() ? true : null">

        <!-- ===========================
            thead
            Guarded rather than left to an empty projected `theadTemplate`:
            that still emits a `<thead>` element, which Bootstrap's table
            styles give a bottom border. `showHeader` removes it outright.
        =========================== -->
        @if (showHeader()) {
            <thead [class]="tableHeadVariantClassStyle()">

                <!-- get thead template -->
                @let theadTemplate = getTemplate('theadTemplate');

                <!-- thead template -->
                @if (theadTemplate; as template) {
                    <ng-container *ngTemplateOutlet="template.templateRef; context: { $implicit: visibleColumns() }" />
                }
                <!-- default thead -->
                @else {
                    <tr>
                        <!-- Selection column -->
                        @if (selectable()) {
                            <th scope="col">
                                <app-table-header-checkbox name="table_select_rows_all" id="table_select_rows_all" />
                            </th>
                        }
                        @for (column of visibleColumns(); track column.id) {

                            <!-- all three bounds share one value, which is what pins the column -->
                            @let headerWidth = getColumnWidth(column);

                            <th
                                scope="col"
                                class="datatable-cell-wrap"
                                [style.width]="headerWidth"
                                [style.min-width]="headerWidth"
                                [style.max-width]="headerWidth"
                                [style.text-align]="column.align"
                                [appTableSortableColumn]="column.field ?? ''"
                            >
                                <div class="d-flex align-items-center gap-2">
                                    <div
                                        [class]="column?.headerClassStyle"
                                        [appTableContentRendered]="column.header"
                                        [dataContext]="{ $implicit: column, column, value: paginatedData()[$index] }"
                                    >
                                    </div>

                                    @if (column.sortable && column.field; as field) {
                                        <app-table-sort-icon [field]="field" />
                                    }
                                </div>
                            </th>
                        }
                    </tr>
                }
            </thead>
        }

        <!-- ===========================
            tbody
        =========================== -->
        <tbody [class]="buildTableBodyClassStyle()">

            <!-- loading (first load only — no existing rows to preserve) -->
            @if (loading() && !hasRows) {
                <tr>
                    <td [attr.colspan]="(selectable() ? 1 : 0) + visibleColumns().length" class="text-center py-4">

                        <!-- loading template -->
                        @let loadingTemplate = getTemplate('loadingTemplate');

                        <ng-container
                            *ngTemplateOutlet="loadingTemplate?.templateRef ?? defaultLoadingTemplate; context: { $implicit: loading() }"/>

                    </td>
                </tr>
            }
            <!-- error -->
            @else if (error()) {
                <tr>
                    <td [attr.colspan]="(selectable() ? 1 : 0) + visibleColumns().length" class="text-center py-4">
                        
                        <!-- error template -->
                        @let errorTemplate = getTemplate('errorTemplate');

                        <ng-container 
                            *ngTemplateOutlet="errorTemplate?.templateRef ?? defaultErrorTemplate; context: { $implicit: error() }"/>
                    </td>
                </tr>
            }
            <!-- data -->
            @else {
                @for (row of paginatedData(); track row.id || $index) {

                    <!-- get tbody template -->
                    @let tbodyTemplate = getTemplate('tbodyTemplate');

                    <!-- tbody template -->
                    @if (tbodyTemplate; as template) {
                        <ng-container *ngTemplateOutlet="template.templateRef; context: { 
                            error: error(),
                            loading: loading(),
                            $implicit: row,
                            index: $index,
                            menuActions: menuActions(),
                            inlineActions: inlineActions(),
                            visibleColumns: visibleColumns(),
                            columnWidths: columnWidths(),
                            dropdownItems: convertActionsToDropdownItems(menuActions(), row),
                        }" />
                    }
                    @else {
                        <tr>
                            <!-- Selection column -->
                            @if (selectable()) {
                                <td style="width: 50px;">
                                    <app-table-checkbox [value]="row" [name]="row.id" [id]="row.id" />
                                </td>
                            }
                            @for (column of visibleColumns(); track column.id || $index; let last = $last) {
                                
                                @let field = column.field ?? '';
                                @let defaultValue = row[field] ?? row;
                                @let body = column.body ?? row[field];
        
                                @if (!isColumnActions(column)) {

                                    <!-- all three bounds share one value, which is what pins the column -->
                                    @let cellWidth = getColumnWidth(column);

                                    <td
                                        [class]="column?.cellClassStyle"
                                        [style.width]="cellWidth"
                                        [style.min-width]="cellWidth"
                                        [style.max-width]="cellWidth"
                                        [style.text-align]="column.align">
                                        <div 
                                            [class]="column?.bodyClassStyle"
                                            class="d-flex align-items-center gap-2"
                                            [appTableContentRendered]="body"
                                            [dataContext]="{ $implicit: row, column, row, value: defaultValue }"
                                        >
                                        </div>
                                    </td>
                                }
                                @else {
                                    <td
                                        class="cell-fit"
                                        [class]="column?.cellClassStyle"
                                        [style.width]="column.width"
                                        [style.text-align]="column.align">

                                        <div class="d-flex align-items-center gap-2">
                                            
                                            <!-- Inline Actions -->
                                            @for (action of inlineActions(); track action.id || $index) {
                                                @if (isActionVisible(action, row)) {
                                                    
                                                    @let buttonOptions = action?.buttonOptions ?? {};
        
                                                    <div 
                                                        [class]="action?.bodyClassStyle"
                                                        class="d-flex justify-content-center align-items-center gap-1"
                                                    >
                                                        <button
                                                            appWaves
                                                            appButton 
                                                            [class]="action?.classStyle"
                                                            [size]="buttonOptions?.size || 'sm'"
                                                            [rounded]="buttonOptions?.rounded ?? true"
                                                            [iconOnly]="buttonOptions?.iconOnly ?? true"
                                                            [variant]="buttonOptions?.variant || 'secondary'" 
                                                            [appearance]="buttonOptions?.appearance || 'outline'"
                                                            [disabled]="isActionDisabled(action, row)"
                                                            (click)="executeAction(action, row)"
                                                        >
                                                            @if (action?.icon) {
                                                                <i class="text-secondary" [class]="action?.icon"></i>
                                                            }
                                                            @else {
                                                                {{ action?.label }}
                                                            }
                                                        </button>
                                                    </div>
                                                }
                                            }
                                            
                                            <!-- Menu Actions -->
                                            @if (menuActions().length > 0) {
                                                
                                                @let dropdownOptions = getColumnDropdownOptions(column);
                                                @let dropdownItems = convertActionsToDropdownItems(menuActions(), row);
        
                                                <app-dropdown
                                                    [items]="dropdownItems"
                                                    [buttonSize]="dropdownOptions?.size || 'sm'"
                                                    [iconOnly]="dropdownOptions?.iconOnly || true"
                                                    [buttonVariant]="dropdownOptions?.variant || 'primary'"
                                                    [buttonAppearance]="dropdownOptions?.appearance || 'text'"
                                                    [icon]="dropdownOptions?.icon || 'fa-solid fa-ellipsis-vertical'"
                                                />
                                            }
                                        </div>
        
                                    </td>
                                }
                                
        
                            }
                        </tr>
                    }

                } 
                <!-- no data -->
                @empty {
                    <tr>
                        <td [attr.colspan]="(selectable() ? 1 : 0) + visibleColumns().length" class="text-center">
                            <!-- no data template -->
                            @let noDataTemplate = getTemplate('noDataTemplate');

                            <ng-container
                                *ngTemplateOutlet="noDataTemplate?.templateRef ?? defaultNoDataTemplate; context: { $implicit: ('TABLE.STATES.NO_DATA' | translate) }"/>
                        </td>
                    </tr>
                }
            }

        </tbody>

        <!-- ===========================
            tfoot
            Guarded for the same reason as the thead above: a table showing a
            single record has no use for the "showing N of total" summary, and
            an empty projected `tfootTemplate` would still leave the element
            and its row border behind.
        =========================== -->
        @if (showFooter()) {
            <tfoot>

                <!-- tfoot template -->
                @let tfootTemplate = getTemplate('tfootTemplate');

                <!-- tfoot template -->
                @if (tfootTemplate; as template) {
                    <ng-container
                        *ngTemplateOutlet="template.templateRef; context: { $implicit: totalRecords() }" />
                }
                <!-- default tfoot -->
                @else {
                    <tr>
                        <td [attr.colspan]="(selectable() ? 1 : 0) + visibleColumns().length">
                            <div class="d-flex align-items-center gap-2">
                                <div class="d-flex align-items-center gap-2" [innerHTML]="'TABLE.PAGINATION.SHOWING_OF_TOTAL_ENTRIES' | translate: { showing: paginatedData().length, total: totalRecords() }">
                                </div>

                                @let showTotalRecords = selectedRows().length > 0;

                                @if (showTotalRecords) {
                                    <div class="d-flex align-items-center gap-2">
                                        <strong>- {{ 'TABLE.FOOTER.SELECTED_ROWS' | translate }}: </strong>
                                        <span>{{ selectedRows().length }}</span>
                                    </div>
                                }
                            </div>
                        </td>
                    </tr>
                }
            </tfoot>
        }
    </table>

    <!-- ===========================
        appBlockUi overlay content
        Only ever rendered while `appBlockUi` above is actually blocking (see
        the tbody comment for why rows stay mounted underneath it instead of
        being replaced by this).
    =========================== -->
    <div #blockElement>

        <!-- loading template -->
        @let loadingTemplate = getTemplate('loadingTemplate');

        <ng-container
            *ngTemplateOutlet="loadingTemplate?.templateRef ?? defaultLoadingTemplate; context: { $implicit: loading() }"/>

    </div>

</div>
<!-- end table responsive wrapper -->

<!-- ===========================
    caption (bottom)
=========================== -->
@if (showCaption() && !captionTop()) {
    <ng-container *ngTemplateOutlet="tableCaption" />
}

<!-- ===========================
    pagination
=========================== -->
@if (paginator()) {
    <div class="d-flex align-items-center gap-5 flex-wrap justify-content-center my-5">

        <!-- Custom pagination template -->
        @let paginationTemplate = getTemplate('paginationTemplate');

        <!-- pagination template -->
        @if (paginationTemplate; as template) {
            <ng-container *ngTemplateOutlet="template.templateRef; context: template.context"></ng-container>
        } @else {
            <!-- Default: app-pagination -->
            <app-pagination
                [rounded]="true"
                [disabled]="frazzled()"
                [currentPage]="currentPage()"
                [itemsPerPage]="rowsPerPage()"
                [totalRecords]="totalRecords()"
                (currentPageChange)="onPageChange($event)"
            />
        }

        <!-- Rows per page selector (next to pagination) -->
        @if (showRowsPerPageSelector()) {
            <div class="d-flex align-items-center gap-2">
                <label for="rowsPerPageSelect" class="mb-0 text-muted small">{{ 'TABLE.PAGINATION.ROWS_PER_PAGE' | translate }}</label>
                <app-select
                    [showLabel]="false"
                    ignoreStatus="valid"
                    name="rowsPerPageSelect"
                    identifier="rowsPerPageSelect"
                    [disabled]="frazzled()"
                    [value]="rowsPerPage()"
                    [items]="rowsPerPageOptions()"
                    (onChange)="onRowsPerPageChange($event)"
                />
            </div>
        }

    </div>
}

<!-- ===========================
    caption template
=========================== -->
<ng-template #tableCaption>

    <!-- get caption template -->
    @let captionTemplate = getTemplate('captionTemplate');

    <div class="table_caption" [class]="captionClassStyle()">

        @if (captionTemplate; as template) {
            <ng-container *ngTemplateOutlet="template.templateRef" />
        }
        @else {
            <div class="table_options d-flex align-items-center gap-2 flex-row-reverse">
                <app-table-search />
            </div>
        }
    </div>
</ng-template>

<!-- ===========================
    default templates
=========================== -->

<!-- default no data template -->
<ng-template #defaultNoDataTemplate let-message>
    <p class="display-6 my-3">{{ message }}</p>
</ng-template>

<!-- default error template -->
<ng-template #defaultErrorTemplate let-error>
    <p class="text-danger my-3">{{ error }}</p>
</ng-template>

<!-- default loading template -->
<ng-template #defaultLoadingTemplate>
    <div class="d-flex align-items-center justify-content-center gap-2 bg-body">
        <app-spin-kit-wave style="--sk-size: 40px;" />
        <strong class="ms-2">{{ 'TABLE.STATES.LOADING' | translate }}</strong>
    </div>
</ng-template>